import { useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useMyReports } from './useMyReports'
import useReadState from './useReadState'
import reportService from '../api/v1/reportService'
import exagoReportService from '../api/v1/exagoReportService'
import { ACTIVE_STATES, NO_RESULT_STATES, resultExpiry } from '../config/queueConfig'
import { EMPTY_MAP, nameJob, reportNameMap, isExagoReportId, withExagoNames } from '../components/reports/queue/jobIdentity'

const REPORT_JOBS = import.meta.env.VITE_REPORT_JOBS === 'true'

/** The state tabs across the top of the queue, in display order. */
export const QUEUE_TABS = [
  { key: 'all', label: 'All' },
  { key: 'running', label: 'Running' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'ready', label: 'Ready' },
  { key: 'failed', label: "Didn't finish" },
]

/**
 * `failed` covers the runs that produced no result, which includes the ones the
 * user STOPPED (QA, 2026-09-03).
 *
 * `cancelled` is a real server state and was in no predicate here, so a cancelled
 * run appeared under **All** and nowhere else — Jordan's "cancelled reports are
 * not showing up in Didn't finish". It reads as `Cancelled` in a grey chip, not
 * as a failure (`JOB_STATES.cancelled`); the tab is where a person looks, the
 * chip is what tells them which of the two happened.
 */
function matchesTab(job, tab) {
  if (tab === 'all') return true
  if (tab === 'running') return ACTIVE_STATES.has(job.effectiveState)
  if (tab === 'scheduled') return job.effectiveState === 'scheduled' || job.effectiveState === 'recurring'
  if (tab === 'ready') return job.effectiveState === 'ready'
  if (tab === 'failed') return NO_RESULT_STATES.has(job.effectiveState)
  return true
}

/**
 * Backs the Queue page (spec 056): the user's jobs, name-resolved, filtered by
 * state tab and free-text search, with per-tab counts.
 *
 * SCOPE (Mine / My group / Everyone) is passed to the SERVER, never applied here
 * (CC-50150 part 2). The list endpoint now takes `?scope=`, and the audience for
 * "My group" is resolved from CB inside the worker — the client never asserts who
 * its peers are. Filtering one user's own rows and labelling the result "Everyone
 * at the firm" would show a partial list as if it were complete, which is why this
 * was gated rather than faked until the server could answer it.
 *
 * `scopeResolved` is false when the server's group lookup failed and it fell back
 * to "mine"; the page must say so rather than implying the group is quiet.
 *
 * Spec 132 — `scheduleNames` (a Map of the caller's OWN schedules, id → name),
 * `schedulesResolved` (that list is a completed read) and `schedulesReadAt` (when that
 * read completed, epoch ms) are optional and come from the caller, which already holds
 * the schedules query; this hook makes no request for them. Pass a STABLE Map
 * (`jobIdentity.EMPTY_MAP` while unresolved): every memo here and the drawer's focus
 * capture are keyed through it. See `nameJob` for why the read time matters.
 */
export function useReportQueue({ tab = 'all', search = '', scope = 'mine', enabled = REPORT_JOBS, scheduleNames = EMPTY_MAP, scheduleRetains = EMPTY_MAP, schedulesResolved = false, schedulesReadAt = null } = {}) {
  const { jobs, loading, refresh, dismiss, dismissAll, markRead, markAllRead, runningCount, readyCount, unreadCount, scopeResolved, error, total, stateCounts, countedAt, resultMaxAgeDays, fetchedAt } =
    useMyReports({ enabled, scope })

  // Resolve report_id → catalog name so rows read "General Ledger", not
  // "revenue-commissions" or "Report 303" (both seen live on QA). Shares the
  // react-query cache key with Reports.jsx, so this is usually free.
  const catalogQuery = useQuery({
    queryKey: ['reports-list'],
    queryFn: () => reportService.list(''),
    staleTime: 5 * 60 * 1000,
    enabled,
  })
  const catalogTabs = catalogQuery.data

  /*
   * Whether the NAMES are trustworthy — a separate question from whether the jobs
   * are (CC-53132).
   *
   * The job list is not react-query at all (`useMyReports` is hand-rolled and has
   * its own `error`), so the `isLoading = isPending && isFetching` trap does not
   * reach it. This catalog read is a real `useQuery`, and it can sit PAUSED
   * indefinitely — default `networkMode: 'online'`, `onlineManager` believing the
   * browser is offline — with `queryFn` never called, nothing thrown and nothing
   * logged. `data` stays undefined, so `nameById` is empty and every row silently
   * degrades to "Report 303" while `search` matches against names we do not have.
   *
   * Bounded, but not nothing: a user searching "Aged A/R" gets no rows and no
   * reason. Surfaced so the page can say "report names couldn't be loaded, so
   * search may be incomplete" instead of implying the queue is empty.
   *
   * `'idle'` is the deliberate feature-gate (`enabled` is `VITE_REPORT_JOBS`), and
   * the whole queue surface is hidden then — so it reads as not-resolved here and
   * that is correct, not a defect to paper over.
   */
  const namesState = useReadState('queue-report-names', catalogQuery)
  const namesResolved = namesState === 'ready'

  // An empty map is a Map-builder answer, not a claim: a paused or failed catalog read
  // is reported through `namesResolved`, never inferred from an empty map.
  // `catalogTabs` is react-query's structurally-shared `data`, so this is stable.
  const catalogNames = useMemo(() => reportNameMap(catalogTabs), [catalogTabs])

  // Spec 192: an Exago run is not in the catalog. Its name comes from the firm's Exago
  // list (the Reports page's own query key, so usually free) — asked only when a listed
  // run IS one, so no other firm's queue makes the request. Unasked or failed, such a
  // row reads "Advanced Report" (`jobDisplayName`), never a raw id.
  const hasExagoRun = useMemo(() => (jobs || []).some((j) => isExagoReportId(j?.reportId)), [jobs])
  const exagoQuery = useQuery({
    queryKey: ['exago-reports'],
    queryFn: () => exagoReportService.list(),
    staleTime: 60_000,
    enabled: enabled && hasExagoRun,
  })
  const exagoReports = exagoQuery.data?.reports
  const reportNames = useMemo(() => withExagoNames(catalogNames, exagoReports), [catalogNames, exagoReports])

  /*
   * Spec 132 — ONE naming rule for every job the page can show, listed or not.
   *
   * Returned as `nameJob` so the page's drawer opener names a DETAIL-ONLY job (a run
   * outside the loaded page) exactly as this list names its rows. Before this, only
   * the list was ever named, so the same run was "Billable Hours Report" when clicked
   * from a row and "Report 190" when reached from a Schedules-page link.
   *
   * The name is the catalog REPORT name (decision 1). The dead schedule-name-first
   * branch that stood here is deleted, not revived: it never fired (nothing populated
   * `scheduleName`), and a PDF masthead titled "CC-53438" says nothing about what the
   * document is. The schedule name is carried as `scheduleName` for the Trigger note.
   *
   * Referentially STABLE across renders unless a map changes — the drawer's inputs
   * depend on its identity.
   *
   * Spec 152 — `scheduleRetains` (schedule id → "last N", `scheduleRetainMap`) rides the
   * same binder, so the retention sentence can say "last 3 runs" on a listed row and a
   * detail-only one alike. Same stability rule as the name maps: pass EMPTY_MAP, not a
   * fresh Map, while unresolved.
   */
  const namer = useCallback(
    (j) => nameJob(j, { reportNames, scheduleNames, scheduleRetains, schedulesResolved, schedulesReadAt }),
    [reportNames, scheduleNames, scheduleRetains, schedulesResolved, schedulesReadAt]
  )

  const named = useMemo(() => jobs.map(namer), [jobs, namer])

  // Expired runs are not LISTED (Jordan, 2026-08-07): the result is gone, so the
  // row is a dead end that pushes live work down the page.
  //
  // Spec 152 — this now includes a schedule's run once the results bucket has aged its
  // object out (`resultExpiresAt`, via `deriveState`). Before, such a run never expired
  // (its `expires_at` is null), so it stayed listed as Ready with Open on offer while its
  // result answered 410. It still reads Expired wherever it is reached: the drawer
  // (Schedules "Last run", Run history, a deep link), Run history and the bell.
  //
  // Deliberately filtered here and NOT dropped from `allJobs`: the drawer resolves
  // the open job out of `allJobs`, and a job can cross its expiry while its drawer
  // is open — leaving it in place means the drawer swaps to the honest "expired"
  // panel instead of the whole drawer vanishing under the user. `deriveState` and
  // the Expired vocabulary stay for exactly that moment.
  const listable = useMemo(() => named.filter((j) => j.effectiveState !== 'expired'), [named])

  /**
   * Tab counts. Server-side when the worker offers them (CC-53049), page-local otherwise.
   *
   * These used to be counted over `listable` — i.e. over whatever slice had been
   * fetched, which was the server's default of 25. On a tenant with 411 runs "Didn't
   * finish" read **0** while ten failures sat one page back. That is the ticket's
   * headline finding, and a bigger page does not fix it; it only moves the boundary.
   *
   * Two things the server cannot answer, so they stay client-side even in the
   * server-counted branch:
   *
   *  - **`expired` is derived client-side** and nothing marks it server-side, so a row
   *    the server counts as `ready` may be expired by the time we render it.
   *
   *    Spec 152 review: a current worker classifies it for us. Its `stateCounts` count a
   *    `ready` row whose result had expired at `countedAt` under `expired` — across the
   *    WHOLE scope, which matters because an aged-out schedule's run (`expires_at`
   *    NULL) is never swept, so the old page-local subtraction left every one beyond
   *    the page in "N ready to view" for good (QA, 2026-10-09: 137 shown, 80 openable).
   *    Here we subtract only the rows on the page that expired SINCE `countedAt` — the
   *    one thing the server could not have seen — and `all` drops everything expired,
   *    since an expired run is never listed.
   *
   *    An older worker (`countedAt` null) still counts them as `ready`, so we subtract
   *    the expired rows we can actually see, as before: approximate beyond the page.
   *  - **`running`** spans several underlying states (`ACTIVE_STATES`), so it is summed
   *    from the breakdown rather than read from one key.
   *
   * `countsAreComplete` tells the page which branch it got, so it can label a page-local
   * count honestly instead of implying it covers everything.
   */
  const { counts, countsAreComplete } = useMemo(() => {
    const pageLocal = () => {
      const c = {}
      for (const t of QUEUE_TABS) c[t.key] = listable.filter((j) => matchesTab(j, t.key)).length
      return c
    }
    if (!stateCounts) return { counts: pageLocal(), countsAreComplete: false }

    const sumOf = (states) => states.reduce((n, s) => n + (stateCounts[s] || 0), 0)
    let expiredNow
    let ready
    if (countedAt != null) {
      // Rows the server counted as `ready` that have expired since it counted them.
      const since = named.filter((j) => {
        if (j.effectiveState !== 'expired' || j.state !== 'ready') return false
        const at = resultExpiry(j)
        return !(at != null && at <= countedAt)
      }).length
      expiredNow = (stateCounts.expired || 0) + since
      ready = Math.max(0, (stateCounts.ready || 0) - since)
    } else {
      expiredNow = named.filter((j) => j.effectiveState === 'expired').length
      ready = Math.max(0, (stateCounts.ready || 0) - expiredNow)
    }
    const c = {
      all: Math.max(0, (total ?? 0) - expiredNow),
      running: sumOf([...ACTIVE_STATES]),
      scheduled: sumOf(['scheduled', 'recurring']),
      ready,
      // Must agree with `matchesTab('failed')` — a count of 2 over a list of 3 is
      // the CC-53049 shape again, one level down.
      failed: sumOf([...NO_RESULT_STATES]),
    }
    return { counts: c, countsAreComplete: true }
  }, [listable, named, stateCounts, countedAt, total])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return listable
      .filter((j) => matchesTab(j, tab))
      .filter((j) => !term || j.displayName?.toLowerCase().includes(term) || String(j.reportId || '').includes(term))
  }, [listable, tab, search])

  return {
    jobs: filtered,
    allJobs: named,
    counts,
    loading,
    refresh,
    dismiss,
    dismissAll,
    markRead,
    markAllRead,
    runningCount,
    readyCount,
    unreadCount,
    scopeResolved,
    // CC-53047 — non-null when the last list read FAILED. The page must gate every
    // empty state AND every count on this: an empty list plus an error is a failed
    // read, and rendering the empty state for it is the whole bug.
    error,
    // CC-53049 — `total` is the server's count for the whole scope; `countsAreComplete`
    // says whether `counts` covers it or only the fetched page.
    total,
    countsAreComplete,
    // Spec 152 review — the bucket's age from the list envelope: a number, or null until a
    // read has answered or when the worker declares none (see `useMyReports`).
    resultMaxAgeDays,
    // When these rows were read from the server (epoch ms), null before the first
    // successful read. The page's time-window filter takes its boundary from this
    // rather than from a render-time clock, so the window means "as of the list you
    // are looking at" and cannot drift from the data it is filtering.
    fetchedAt,
    /*
     * CC-53132 — whether `counts` describes a read that actually happened.
     *
     * Distinct from `countsAreComplete`, and both are needed: complete says the
     * numbers cover the whole scope rather than one page; RESOLVED says there are
     * numbers at all. On a failed first read `stateCounts` is null, so the memo
     * counts over an empty `listable` and every tab badge reads 0 —
     * `QueueStateTabs` renders `counts[key] ?? 0`, so five confident zeros sit
     * next to the page's own "Counts unavailable". A count is a claim; gate the
     * badges on this and render an em dash instead.
     *
     * `!loading` is part of it because a count before the first read lands is the
     * same claim as a count after a failed one. (The very first synchronous render
     * — before `useMyReports`' effect sets `loading` — cannot be caught from out
     * here; that needs a "has read once" flag inside `useMyReports`, which is not
     * this file.)
     */
    countsResolved: !error && !loading,
    // CC-53132 — the catalog read behind `displayName`. False means rows may be
    // named "Report 303" and search cannot match on names, NOT that the queue is
    // empty.
    namesResolved,
    namesState,
    // Spec 132 — name a job that is NOT in `allJobs` (the drawer's detail-only path)
    // with the same maps and the same rule. Stable; see `namer` above.
    nameJob: namer,
  }
}

export default useReportQueue
